import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Center, Group, Loader, Select, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

interface RecapData {
    jobs: {
        total: number;
        active: number;
        inactive: number;
    };
    applications: {
        total: number;
        by_career: {
            career: string;
            total: number;
        }[];
    };
    users: {
        total: number;
        by_career: {
            career: string;
            total: number;
        }[];
    };
    by_year?: {
        jobs: {
            year: number;
            total: number;
            active: number;
            inactive: number;
        }[];
        applications: {
            year: number;
            total: number;
        }[];
        users_by_career: {
            year: number;
            career: string;
            total: number;
        }[];
        applications_by_career: {
            year: number;
            career: string;
            total: number;
        }[];
    };
}

const ModeratorStatsPage: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [data, setData] = useState<RecapData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [yearFilter, setYearFilter] = useState<string>('ALL');

    useEffect(() => {
        loadRecap();
    }, [yearFilter]);

    const loadRecap = async () => {
        try {
            setLoading(true);
            setError(null);
            const params: Record<string, string> = { group_by_year: 'true' };
            if (yearFilter !== 'ALL') {
                params.year = yearFilter;
            }
            const response = await api.getJobsRecap(params);
            setData(response as RecapData);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('stats.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const yearOptions = useMemo(() => {
        const years = new Set<number>();
        data?.by_year?.jobs.forEach((item) => years.add(item.year));
        data?.by_year?.applications.forEach((item) => years.add(item.year));
        const sorted = Array.from(years).sort((a, b) => b - a);
        return [
            { value: 'ALL', label: t('stats.allYears') },
            ...sorted.map((year) => ({ value: String(year), label: String(year) })),
        ];
    }, [data, t]);

    if (user?.user_type !== 'MODERATOR') {
        return (
            <Card withBorder padding="xl" radius="lg" shadow="sm">
                <Title order={3}>{t('common.accessRestricted')}</Title>
                <Text c="dimmed">{t('stats.onlyModerators')}</Text>
            </Card>
        );
    }

    if (loading) {
        return (
            <Center py="xl">
                <Loader />
            </Center>
        );
    }

    const careerLabelMap: Record<string, string> = {
        MUSICOTERAPIA: t('careers.musicotherapy'),
        PSICOLOGIA: t('careers.psychology'),
        PROFESORADO_PSICOLOGIA: t('careers.psychologyTeaching'),
        TERAPIA_OCUPACIONAL: t('careers.occupationalTherapy'),
        UNSPECIFIED: t('stats.careerUnspecified'),
    };

    const buildCareerMatrix = (items: { year: number; career: string; total: number }[]) => {
        if (!items.length) return null;
        const careerOrder = [
            'MUSICOTERAPIA',
            'PSICOLOGIA',
            'PROFESORADO_PSICOLOGIA',
            'TERAPIA_OCUPACIONAL',
            'UNSPECIFIED',
        ];
        const yearsSet = new Set<number>();
        const careersSet = new Set<string>();
        const valuesByYear: Record<number, Record<string, number>> = {};

        items.forEach((item) => {
            yearsSet.add(item.year);
            careersSet.add(item.career);
            if (!valuesByYear[item.year]) {
                valuesByYear[item.year] = {};
            }
            valuesByYear[item.year][item.career] = item.total;
        });

        const orderedCareers = careerOrder.filter((career) => careersSet.has(career));
        const extraCareers = Array.from(careersSet)
            .filter((career) => !careerOrder.includes(career))
            .sort((a, b) => a.localeCompare(b));
        const careers = [...orderedCareers, ...extraCareers];
        const years = Array.from(yearsSet).sort((a, b) => b - a);

        return {
            years,
            careers,
            valuesByYear,
        };
    };

    const usersByCareerMatrix = buildCareerMatrix(data?.by_year?.users_by_career || []);
    const applicationsByCareerMatrix = buildCareerMatrix(data?.by_year?.applications_by_career || []);

    return (
        <Card withBorder padding="xl" radius="lg" shadow="sm">
            <Stack gap="lg">
                <Group justify="space-between" align="center" wrap="wrap">
                    <Title order={2}>{t('stats.title')}</Title>
                    <Select
                        label={t('stats.filterYear')}
                        data={yearOptions}
                        value={yearFilter}
                        onChange={(value) => setYearFilter(value || 'ALL')}
                        style={{ width: '100%', maxWidth: 200 }}
                    />
                </Group>
                {error && (
                    <Alert icon={<IconAlertCircle size={16} />} title={t('stats.errorTitle')} color="red" radius="md">
                        {error}
                    </Alert>
                )}
                {data && (
                    <Stack gap="md">
                        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                            <Card withBorder padding="md" radius="md">
                                <Text size="sm" c="dimmed">{t('stats.jobsTotal')}</Text>
                                <Title order={3}>{data.jobs.total}</Title>
                            </Card>
                            <Card withBorder padding="md" radius="md">
                                <Text size="sm" c="dimmed">{t('stats.jobsActive')}</Text>
                                <Title order={3}>{data.jobs.active}</Title>
                            </Card>
                            <Card withBorder padding="md" radius="md">
                                <Text size="sm" c="dimmed">{t('stats.jobsInactive')}</Text>
                                <Title order={3}>{data.jobs.inactive}</Title>
                            </Card>
                            <Card withBorder padding="md" radius="md">
                                <Text size="sm" c="dimmed">{t('stats.usersTotal')}</Text>
                                <Title order={3}>{data.users.total}</Title>
                            </Card>
                        </SimpleGrid>
                        <Card withBorder padding="md" radius="md">
                            <Text size="sm" c="dimmed">{t('stats.applicationsTotal')}</Text>
                            <Title order={3}>{data.applications.total}</Title>
                        </Card>
                        {data.by_year && (
                            <Stack gap="md">
                                <Title order={4}>{t('stats.jobsTableTitle')}</Title>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table withTableBorder withColumnBorders striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>{t('stats.tableYear')}</Table.Th>
                                                <Table.Th>{t('stats.tableTotal')}</Table.Th>
                                                <Table.Th>{t('stats.tableActive')}</Table.Th>
                                                <Table.Th>{t('stats.tableInactive')}</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {data.by_year.jobs.map((item) => (
                                                <Table.Tr key={`jobs-${item.year}`}>
                                                    <Table.Td>{item.year}</Table.Td>
                                                    <Table.Td>{item.total}</Table.Td>
                                                    <Table.Td>{item.active}</Table.Td>
                                                    <Table.Td>{item.inactive}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                                <Title order={4}>{t('stats.applicationsTableTitle')}</Title>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table withTableBorder withColumnBorders striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>{t('stats.tableYear')}</Table.Th>
                                                <Table.Th>{t('stats.tableTotal')}</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {data.by_year.applications.map((item) => (
                                                <Table.Tr key={`apps-${item.year}`}>
                                                    <Table.Td>{item.year}</Table.Td>
                                                    <Table.Td>{item.total}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                                {usersByCareerMatrix && (
                                    <>
                                        <Title order={4}>{t('stats.usersByCareerTitle')}</Title>
                                        <div style={{ overflowX: 'auto' }}>
                                            <Table withTableBorder withColumnBorders striped highlightOnHover>
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>{t('stats.tableYear')}</Table.Th>
                                                        {usersByCareerMatrix.careers.map((career) => (
                                                            <Table.Th key={`user-career-head-${career}`}>
                                                                {careerLabelMap[career] || career}
                                                            </Table.Th>
                                                        ))}
                                                    </Table.Tr>
                                                </Table.Thead>
                                                <Table.Tbody>
                                                    {usersByCareerMatrix.years.map((year) => (
                                                        <Table.Tr key={`user-career-row-${year}`}>
                                                            <Table.Td>{year}</Table.Td>
                                                            {usersByCareerMatrix.careers.map((career) => (
                                                                <Table.Td key={`user-career-${year}-${career}`}>
                                                                    {usersByCareerMatrix.valuesByYear[year]?.[career] || 0}
                                                                </Table.Td>
                                                            ))}
                                                        </Table.Tr>
                                                    ))}
                                                </Table.Tbody>
                                            </Table>
                                        </div>
                                    </>
                                )}
                                {applicationsByCareerMatrix && (
                                    <>
                                        <Title order={4}>{t('stats.applicationsByCareerTitle')}</Title>
                                        <div style={{ overflowX: 'auto' }}>
                                            <Table withTableBorder withColumnBorders striped highlightOnHover>
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>{t('stats.tableYear')}</Table.Th>
                                                        {applicationsByCareerMatrix.careers.map((career) => (
                                                            <Table.Th key={`app-career-head-${career}`}>
                                                                {careerLabelMap[career] || career}
                                                            </Table.Th>
                                                        ))}
                                                    </Table.Tr>
                                                </Table.Thead>
                                                <Table.Tbody>
                                                    {applicationsByCareerMatrix.years.map((year) => (
                                                        <Table.Tr key={`app-career-row-${year}`}>
                                                            <Table.Td>{year}</Table.Td>
                                                            {applicationsByCareerMatrix.careers.map((career) => (
                                                                <Table.Td key={`app-career-${year}-${career}`}>
                                                                    {applicationsByCareerMatrix.valuesByYear[year]?.[career] || 0}
                                                                </Table.Td>
                                                            ))}
                                                        </Table.Tr>
                                                    ))}
                                                </Table.Tbody>
                                            </Table>
                                        </div>
                                    </>
                                )}
                            </Stack>
                        )}
                        {data.users.by_career.length > 0 && (
                            <Stack gap="md">
                                <Title order={4}>{t('stats.usersByCareerTitle')}</Title>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table withTableBorder withColumnBorders striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>{t('stats.tableCareer')}</Table.Th>
                                                <Table.Th>{t('stats.tableTotal')}</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {data.users.by_career.map((item) => (
                                                <Table.Tr key={`career-${item.career}`}>
                                                    <Table.Td>{careerLabelMap[item.career] || item.career}</Table.Td>
                                                    <Table.Td>{item.total}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                            </Stack>
                        )}
                        {data.applications.by_career.length > 0 && (
                            <Stack gap="md">
                                <Title order={4}>{t('stats.applicationsByCareerTitle')}</Title>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table withTableBorder withColumnBorders striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>{t('stats.tableCareer')}</Table.Th>
                                                <Table.Th>{t('stats.tableTotal')}</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {data.applications.by_career.map((item) => (
                                                <Table.Tr key={`app-career-${item.career}`}>
                                                    <Table.Td>{careerLabelMap[item.career] || item.career}</Table.Td>
                                                    <Table.Td>{item.total}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        </Card>
    );
};

export default ModeratorStatsPage;
